/**
 * Safe print helper — avoids `document.write` (XSS risk).
 *
 * Uses a hidden, sandboxed iframe with `srcdoc` to render the page,
 * then triggers the browser print dialog. The iframe is auto-removed
 * after a short delay so it doesn't accumulate in the DOM.
 *
 * Notes on safety:
 *  - The caller is responsible for ensuring `html` does not embed
 *    untrusted user content without escaping. Use this helper for
 *    server-supplied / app-generated HTML only.
 *  - `sandbox="allow-modals allow-same-origin"` is required so the
 *    iframe can call `print()` against its parent's document; we
 *    intentionally do NOT allow `allow-scripts` — print docs should
 *    be pure markup + CSS.
 */
export function printHtmlSafely(html, { onError } = {}) {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.setAttribute("title", "Print");
  iframe.setAttribute("sandbox", "allow-modals allow-same-origin");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.style.visibility = "hidden";
  iframe.srcdoc = html;

  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    try {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    } catch (e) {
      // ignore — iframe might already be detached
    }
  };

  iframe.onload = () => {
    try {
      const cw = iframe.contentWindow;
      if (!cw) {
        cleanup();
        if (onError) onError(new Error("iframe contentWindow unavailable"));
        return;
      }
      // Some browsers need a tick before print is ready
      setTimeout(() => {
        try {
          cw.focus();
          cw.print();
        } catch (e) {
          if (onError) onError(e);
        }
        // Give the print dialog time to capture the document before removal
        setTimeout(cleanup, 1500);
      }, 100);
    } catch (e) {
      cleanup();
      if (onError) onError(e);
    }
  };

  document.body.appendChild(iframe);
}
